/**
 * SubscribeModal — subscription confirmation dialog
 *
 * Replaces cycle-1 console-log stub. Renders a modal overlay with three phases:
 *   idle/submitting → confirm screen
 *   success         → confirmation screen
 *   error           → retry screen
 *
 * Uses useSubscription hook (POST /api/broker/v1/subscriptions).
 *
 * wp-order-surface-vertical-broker: the idle phase now carries a PLAN CHOICE
 * (Trial / Professional), read from the registry plans endpoint via usePlans — the
 * same endpoint and shape the detail overlay uses. The chosen plan's price is
 * disclosed through CostDisclosure, which prices it via PricingLabel (SL-R-009): a
 * Professional plan shows its real rate, a Trial shows a time-bounded badge and
 * never a fabricated zero cost. This is the Order surface of the vertical broker;
 * ordering is gated upstream by auth/surfaceAccess canOrderForTenant.
 *
 * Refs: DOPLA-602, DOPLA-901, design-contract §11 (Binding Lifecycle, order variant)
 */

import { useEffect, useState, type MouseEvent } from 'react';
import type { TileConfig } from '../config/schema';
import { useAuth } from '../auth/KeycloakProvider';
import { useSubscription } from '../hooks/useSubscription';
import { usePlans } from '../hooks/usePlans';
import CostDisclosure from './CostDisclosure';
import PricingLabel from './PricingLabel';
import { t } from '../i18n';

interface SubscribeModalProps {
  tile: TileConfig;
  onClose: () => void;
}

export default function SubscribeModal({ tile, onClose }: SubscribeModalProps) {
  const { token, getToken } = useAuth();
  const plansState = usePlans(tile.id, getToken);
  const plans = plansState.tag === 'loaded' ? plansState.plans : [];

  // Default to the plan the registry marks default, else the first offered. The
  // caller may switch; a null selection only survives while plans are still loading.
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  useEffect(() => {
    if (plans.length === 0) return;
    setSelectedPlanId((cur) =>
      cur && plans.some((p) => p.id === cur)
        ? cur
        : (plans.find((p) => p.is_default) ?? plans[0]).id,
    );
  }, [plans]);
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;

  const { state, trigger, reset } = useSubscription(tile.id, token, selectedPlanId);

  // Close on Escape key
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'submitting') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state, onClose]);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && state !== 'submitting') onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-modal-title"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-8)',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--border-default)',
          width: '100%',
          maxWidth: '440px',
          padding: 'var(--sp-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {(state === 'idle' || state === 'submitting') && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow">{t('subscribeModal.eyebrow')}</p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {/* Inline substitution — avoids a runtime t() template engine */}
                {t('subscribeModal.title').replace('{{service}}', tile.title)}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.body')}
              </p>
            </div>

            {/* Plan choice — Trial / Professional, read from the registry plans
                endpoint (usePlans). The catalogue and this modal ask the same
                endpoint the same way. */}
            <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <legend className="dp-eyebrow" style={{ padding: 0, marginBottom: 'var(--sp-2)' }}>
                {t('subscribeModal.planEyebrow')}
              </legend>

              {(plansState.tag === 'idle' || plansState.tag === 'loading') && (
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)', margin: 0 }}>
                  {t('subscribeModal.plansLoading')}
                </p>
              )}

              {plansState.tag === 'error' && (
                <p role="alert" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--dp-crimson-600)', margin: 0 }}>
                  {t('subscribeModal.plansError')}
                </p>
              )}

              {plansState.tag === 'loaded' && plans.length === 0 && (
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)', margin: 0 }}>
                  {t('subscribeModal.noPlans')}
                </p>
              )}

              {plans.map((plan) => {
                const currency = Object.keys(plan.pricing)[0] ?? null;
                const pricing = currency ? plan.pricing[currency] : null;
                return (
                  <label
                    key={plan.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--sp-3)',
                      padding: 'var(--sp-3)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                      <input
                        type="radio"
                        name="subscribe-plan"
                        value={plan.id}
                        checked={selectedPlanId === plan.id}
                        onChange={() => setSelectedPlanId(plan.id)}
                        disabled={state === 'submitting'}
                      />
                      <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, color: 'var(--fg-1)' }}>
                        {plan.name}
                      </span>
                    </span>
                    {currency && pricing && <PricingLabel currency={currency} pricing={pricing} />}
                  </label>
                );
              })}
            </fieldset>

            {/* The cost of the chosen plan, disclosed through the audited component —
                a real rate for a paid plan, a time-bounded badge for a trial. */}
            {selectedPlan && <CostDisclosure plan={selectedPlan} />}

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button
                className="dp-btn dp-btn--ghost"
                onClick={onClose}
                disabled={state === 'submitting'}
              >
                {t('destructive.cancel')}
              </button>
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => void trigger()}
                disabled={state === 'submitting' || (plans.length > 0 && !selectedPlan)}
                style={{ opacity: state === 'submitting' ? 0.7 : 1 }}
              >
                {state === 'submitting'
                  ? t('subscribeModal.submitting')
                  : t('subscribeModal.confirm')}
              </button>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow">{t('subscribeModal.successEyebrow')}</p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {t('subscribeModal.successTitle')}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.successBody')}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--primary" onClick={onClose}>
                {t('subscribeModal.done')}
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow" style={{ color: 'var(--dp-crimson-600)' }}>
                {t('subscribeModal.errorEyebrow')}
              </p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {t('subscribeModal.errorTitle')}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.errorBody')}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--ghost" onClick={onClose}>
                {t('destructive.cancel')}
              </button>
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => { reset(); void trigger(); }}
              >
                {t('subscribeModal.retry')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
